import { useState } from 'react';
import { Link } from 'react-router-dom';
import { faqItems, type FaqItem } from '@/mocks/faq';

const CATEGORY_ORDER = ['Cost', 'Broker & setup', 'Risk & results'] as const;

const CATEGORY_META: Record<string, { icon: string; blurb: string }> = {
  Cost: {
    icon: 'ri-price-tag-3-line',
    blurb: 'What it costs and what you need to start',
  },
  'Broker & setup': {
    icon: 'ri-plug-line',
    blurb: 'Your account, broker and getting it running',
  },
  'Risk & results': {
    icon: 'ri-shield-check-line',
    blurb: 'How risk is controlled and where the numbers come from',
  },
};

const slugify = (value: string) => value.toLowerCase().replace(/[^a-z0-9]+/g, '-');

function AccordionRow({ item, open, onToggle }: { item: FaqItem; open: boolean; onToggle: () => void }) {
  const buttonId = `${item.id}-button`;
  const panelId = `${item.id}-panel`;

  return (
    <div
      id={item.id}
      className={`scroll-mt-28 rounded-lg border transition-colors duration-200 ${
        open ? 'border-accent-300 bg-accent-50' : 'border-background-200 bg-background-50 hover:border-background-300'
      }`}
    >
      <h3>
        <button
          type="button"
          id={buttonId}
          onClick={onToggle}
          aria-expanded={open}
          aria-controls={panelId}
          className="w-full flex items-center justify-between gap-4 px-5 py-4 md:py-5 text-left cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2 focus-visible:ring-offset-background-50 rounded-lg"
        >
          <span className="font-heading text-sm md:text-base font-semibold text-foreground-950">
            {item.question}
          </span>
          <span
            className={`shrink-0 w-7 h-7 flex items-center justify-center rounded-full transition-all duration-300 ${
              open ? 'bg-accent-500 text-primary-950 rotate-180' : 'bg-background-200 text-foreground-700'
            }`}
          >
            <i className="ri-arrow-down-s-line text-lg" aria-hidden="true"></i>
          </span>
        </button>
      </h3>
      {/* Auto-height reveal via grid rows — no fixed max-height, so longer
          answers can never be clipped. */}
      <div
        id={panelId}
        role="region"
        aria-labelledby={buttonId}
        className={`grid transition-all duration-300 ease-out ${
          open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        }`}
      >
        <div className="overflow-hidden">
          <div className="px-5 pb-5">
            <p className="text-xs md:text-sm leading-relaxed text-foreground-600">{item.answer}</p>
            {item.link ? (
              <Link
                to={item.link.to}
                tabIndex={open ? 0 : -1}
                className="mt-3 inline-flex items-center gap-1.5 text-xs md:text-sm font-semibold text-accent-700 hover:text-accent-900 transition-colors whitespace-nowrap"
              >
                {item.link.label}
                <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </Link>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function FaqAccordion() {
  const categories = CATEGORY_ORDER.map((category) => ({
    category,
    meta: CATEGORY_META[category],
    items: faqItems.filter((item) => item.category === category),
  })).filter((group) => group.items.length > 0);

  const firstItemId = categories[0]?.items[0]?.id ?? null;
  const [openId, setOpenId] = useState<string | null>(firstItemId);
  const toggleItem = (id: string) => setOpenId((prev) => (prev === id ? null : id));

  return (
    <div className="flex flex-col gap-10">
      {categories.map((group) => {
        const headingId = `faq-cat-${slugify(group.category)}`;
        return (
        <section key={group.category} aria-labelledby={headingId}>
          <div className="flex items-center gap-3">
            <span className="shrink-0 w-9 h-9 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-900">
              <i className={`${group.meta.icon} text-lg`} aria-hidden="true"></i>
            </span>
            <div>
              <h2
                id={headingId}
                className="font-heading text-sm md:text-base font-bold text-foreground-950"
              >
                {group.category}
              </h2>
              <p className="text-xs text-foreground-500">{group.meta.blurb}</p>
            </div>
          </div>
          <div className="mt-4 flex flex-col gap-3">
            {group.items.map((item) => (
              <AccordionRow
                key={item.id}
                item={item}
                open={openId === item.id}
                onToggle={() => toggleItem(item.id)}
              />
            ))}
          </div>
        </section>
        );
      })}
    </div>
  );
}